import { useEffect, useState, useRef } from 'react';
import { Grid, Typography, Button, Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { setPrenotaNewEdificioEZona } from '../../../redux/reducers/prenotazioneNew-reducer';
import axios from 'axios';
import { GetEdificiPerPrenotazione } from '../../../const/urlAction';
import { useTranslation } from 'react-i18next';
import { CDCard, CDCardContent } from '../../../const/CDCardStyles';
import defaultBuildingPicture from '../../../assests/defaultBuilding.png';
import isEdificioOpenInOrarioPrenotazione from '../../../functions/stepper/prenotazioneEdificioEZona/isEdificioOpenInOrarioPrenotazione';
import moment from 'moment';
import InlineLoader from '../../UI/spinner/InlineLoader';
import { combineDateAndTime, formatLocalISO } from '../../../functions/formatDate';
import '../styles/ViewEdificiNew.css';

const urlBase = process.env.REACT_APP_URL;

export default function ViewEdificiNew() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { selectedDate, selectedStartTime, selectedEndTime, requestVersion } = useSelector(state => state.prenotazioneNew);
    const userDate = useSelector(state => state.userDate);
    const [edificiList, setEdificiList] = useState([]);
    const [loading, setLoading] = useState(false);
    const hasLoadedOnce = useRef(false);

    useEffect(() => {
        let isMounted = true;
        
        (async () => {
            setLoading(true);
            const { action, method } = GetEdificiPerPrenotazione;
            const url = urlBase + action;
             
            const startDate = combineDateAndTime(selectedDate, selectedStartTime);
            const endDate = combineDateAndTime(selectedDate, selectedEndTime);        

            const body = {
                idUtentePrenotazione: [userDate?.idUtente],
                startDate: formatLocalISO(startDate),
                endDate: formatLocalISO(endDate)
            };
             
            try {
                const response = await axios({
                    method: method,
                    url,
                    data: body,
                    headers: { "Content-Type": "application/json" },
                });
                
                if (isMounted) {
                    setEdificiList(response.data.result || []);
                    hasLoadedOnce.current = true;
                }
            } catch (error) {
                console.error(error);
            } finally {
                if (isMounted) setLoading(false);
            }
        })();
        
        return () => { isMounted = false; };
    }, [requestVersion, selectedDate, selectedStartTime, selectedEndTime]); 

    const handleSelectZona = (edificio, zona) => {
        dispatch(setPrenotaNewEdificioEZona({ edificio, zona }));
    };

    return (
        <Grid container spacing={2} className="view-edifici-new-container">
            {loading && !hasLoadedOnce.current ? (
                <Grid item xs={12}>
                    <InlineLoader message={t("load_buildings")} />
                </Grid>
            ) : edificiList.length > 0 ? (
                edificiList.map((edificio, index) => {
                    const [isEdificioOpen, openMsg] = isEdificioOpenInOrarioPrenotazione(
                        t, 
                        edificio, 
                        moment(selectedDate).format("YYYY-MM-DD"), 
                        moment(selectedStartTime).format("HH:mm"), 
                        moment(selectedEndTime).format("HH:mm")
                    );

                    return (
                        <Grid item xs={12} key={edificio.idEdificio || edificio.id || index}>
                            <CDCard 
                                className={`${isEdificioOpen ? "" : "stepper-edificio-close"} view-edifici-new-card ${loading ? "view-edifici-new-card--loading" : ""}`} 
                            >
                                <Box className="view-edifici-new-image-wrapper">
                                    <img
                                        alt="Foto edificio"
                                        src={edificio.foto ? "data:image/png;base64," + edificio.foto : defaultBuildingPicture}
                                        className="view-edifici-new-image"
                                    />
                                    {(!edificio.foto || !isEdificioOpen) && (
                                        <Box className="view-edifici-new-overlay">
                                            <Typography variant="body2" className="view-edifici-new-overlay-text">
                                                {t(!isEdificioOpen ? openMsg : "Picture_not_available")}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                                <CDCardContent>
                                    <Typography variant="subtitle1" sx={{ color: 'rgba(0, 0, 0, 0.6)', mb: 0.5 }}>
                                        {edificio.denominazione || edificio.nome}
                                        <Typography component="span" sx={{ color: 'text.secondary', ml: 1, fontWeight: 'normal' }}>
                                            ({edificio.sigla})
                                        </Typography>
                                    </Typography>
                                    {edificio.indirizzo && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {edificio.indirizzo}
                                        </Typography>
                                    )}
                                    <Grid container spacing={1}>
                                        {edificio.zone && edificio.zone.map((zona, idx) => (
                                            <Grid item key={idx}>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={!isEdificioOpen || !zona.hasElements}
                                                    onClick={() => handleSelectZona(edificio, zona)}
                                                    sx={{ textTransform: 'uppercase' }}
                                                >
                                                    {`${zona.denominazione} (${zona.totPostazioniOccupate}/${zona.totPostazioniDisponibili})`}
                                                </Button>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </CDCardContent>
                            </CDCard>
                        </Grid>
                    );
                })
            ) : (
                !loading && hasLoadedOnce.current && (
                    <Grid item xs={12} className="view-edifici-new-no-data">
                        <Typography variant="body1">{t("no_buildings_available")}</Typography>
                    </Grid>
                )
            )}
        </Grid>
    );
}